import { ErrorHandler, NgModule } from '@angular/core';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { BrowserModule, HAMMER_GESTURE_CONFIG, HammerModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { RouterModule, Routes } from '@angular/router';
import { JwtModule } from '@auth0/angular-jwt';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TooltipConfig } from 'ngx-bootstrap/tooltip';
import { ModalModule } from 'ngx-bootstrap/modal';
import { CookieService } from 'ngx-cookie-service';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { ToastrModule } from 'ngx-toastr';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { ColorPickerModule } from '@iplab/ngx-color-picker';
import { B2BWebShopSharedModule, AuthenticationService_TOKEN, LIB_API_BASE_URL, getTooltipDefaultConfig, GlobalErrorHandler, HttpErrorHandlerService, LocalStorageWrapper, AuthGuard, VestraUserService_TOKEN, CoreService_TOKEN, JsonCacheInterceptor, HammerConfig, AuthInterceptor, SignalRHeaderInterceptor, NoAuthGuard } from 'lib/Vestra.shared';

import { API_BASE_URL, AuthenticationService, CoreService, VestraUserService } from './services/api-services';
import { environment } from '../environments/environment';
import { AdminGuard } from './shared/guards/admin.guard';
import { AnyGuard } from './shared/guards/any.guard';

import { AppComponent } from './app.component';
import { HomeComponent } from './main/home/home.component';
import { MenuComponent } from './main/menu/menu.component';

import { LoginUserComponent } from './authentication/login-user/login-user.component';
import { ForgotPasswordComponent } from './authentication/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './authentication/reset-password/reset-password.component';
import { ChangePasswordComponent } from './authentication/change-password/change-password.component';

import { UserListComponent } from './pages/core/user/user-list/user-list.component';
import { UserEditComponent } from './pages/core/user/user-edit/user-edit.component';
import { SystemParameterListComponent } from './pages/core/system-parameter/system-parameter-list/system-parameter-list.component';
import { SystemParameterEditComponent } from './pages/core/system-parameter/system-parameter-edit/system-parameter-edit.component';
import { ResourceListComponent } from './pages/core/resource/resource-list/resource-list.component';
import { ResourceEditComponent } from './pages/core/resource/resource-edit/resource-edit.component';
import { BusinessLogListComponent } from './pages/core/business-log/business-log-list/business-log-list.component';
import { VersionInfoComponent } from './pages/core/system-parameter/version-info/version-info.component';

import { CountryListComponent } from './pages/core/country/country-list/country-list.component';

import { LanguageListComponent } from './pages/core/language-list/language-list.component';
import { LanguageEditComponent } from './pages/core/language-edit/language-edit.component';

import { NotificationComponent } from './pages/core/notification/notification.component';
import { NotificationCreateComponent } from './pages/core/notification/notification-create/notification-create.component';
import { NotificationCreateEmailComponent } from './pages/core/notification/notification-create/notification-create-email/notification-create-email.component';
import { NotificationSentMessageListComponent } from './pages/core/notification/notification-sent-message/notification-sent-message-list/notification-sent-message-list.component';
import { NotificationSentMessageEditComponent } from './pages/core/notification/notification-sent-message/notification-sent-message-edit/notification-sent-message-edit.component';



import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';

import 'prismjs';
import 'prismjs/components/prism-typescript.min.js';
import 'prismjs/plugins/line-numbers/prism-line-numbers.js';
import 'prismjs/plugins/line-highlight/prism-line-highlight.js';
import { MarkdownModule } from 'ngx-markdown';

import { DataTablesModule } from 'angular-datatables';
import { NgxJsonViewerModule } from 'ngx-json-viewer';

import { EmailListComponent } from './pages/core/email/email-list/email-list.component';
import { EmailEditComponent } from './pages/core/email/email-edit/email-edit.component';
import { FrontendDisplayHtmlEditComponent } from './pages/core/frontend-display-html/frontend-display-html-edit/frontend-display-html-edit.component';
import { FrontendDisplayHtmlListComponent } from './pages/core/frontend-display-html/frontend-display-html-list/frontend-display-html-list.component';

import { HtmlManagementEditComponent } from './pages/core/html-management/html-management-edit/html-management-edit.component';
import { CouponListComponent } from './pages/coupon/coupon-list/coupon-list.component';
import { CouponEditComponent } from './pages/coupon/coupon-edit/coupon-edit.component';

import { StravaWebhookListComponent } from './pages/strava/strava-webhook/strava-webhook-list/strava-webhook-list.component';
import { StravaActivityViewComponent } from './pages/strava/strava-activity/strava-activity-view/strava-activity-view.component';

import { AllCouponListComponent } from './pages/coupon-tabpage/filter-coupon-list/filter-coupon-list.component';
import { VestraUserListComponent } from './pages/vestra-user/vestra-user-list/vestra-user-list.component';
import { VestraUserSearchComponent } from './pages/vestra-user/vestra-user-search/vestra-user-search.component';
import { VestraUserSelectComponent } from './pages/vestra-user/vestra-user-select/vestra-user-select.component';
import { CouponTabpageComponent } from './pages/coupon-tabpage/coupon-tabpage.component';
import { PartnerTabpageComponent } from './pages/partner-tabpage/partner-tabpage.component';
import { PartnerListComponent } from './pages/partner-tabpage/partner/partner-list/partner-list.component';
import { PartnerEditComponent } from './pages/partner-tabpage/partner/partner-edit/partner-edit.component';
import { PartnerReportComponent } from './pages/partner-tabpage/partner-report/partner-report.component';

import { UserCouponReportComponent } from './pages/user-coupon/user-coupon-report/user-coupon-report.component';

import { NotificationResultComponent } from './pages/core/notification/notification-result/notification-result.component';


export function tokenGetter() {
    return LocalStorageWrapper.getToken();
}

export function createTranslateLoader(http: HttpClient) {
    return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

const routes: Routes = [
    { path: '', pathMatch: 'full', redirectTo: 'authentication/login' },
    {
        path: 'home',
        pathMatch: 'full',
        component: HomeComponent,
        canActivate: [AuthGuard],
    },
    {
        path: 'authentication',
        children: [
            { path: 'login', component: LoginUserComponent },
            { path: 'forgotpassword', component: ForgotPasswordComponent },
            { path: 'resetpassword', component: ResetPasswordComponent },
            {
                path: 'changepassword',
                component: ChangePasswordComponent,
                canActivate: [AuthGuard],
            },
        ],
    },
    {
        path: 'administration',
        children: [
            {
                path: 'coupons',
                component: CouponTabpageComponent,
                canActivate: [AuthGuard, AdminGuard],
            },
            {
                path: 'user-list',
                component: UserListComponent,
                canActivate: [AuthGuard, AdminGuard],
            },
            {
                path: 'system-parameter-list',
                component: SystemParameterListComponent,
                canActivate: [AuthGuard, AdminGuard],
            },
            {
                path: 'language-list',
                component: LanguageListComponent,
                canActivate: [AuthGuard, AdminGuard],
            },
            {
                path: 'resource-list',
                component: ResourceListComponent,
                canActivate: [AuthGuard, AdminGuard],
            },
            {
                path: 'email-list',
                component: EmailListComponent,
                canActivate: [AuthGuard, AdminGuard],
            },
            {
                path: 'frontend-display-html-list',
                component: FrontendDisplayHtmlListComponent,
                canActivate: [AuthGuard, AdminGuard],
            },
            {
                path: 'partner-list',
                component: PartnerTabpageComponent,
                canActivate: [AuthGuard, AdminGuard],
            },
            {
                path: 'coupon-list/:id',
                component: CouponListComponent,
                canActivate: [AuthGuard, AdminGuard],
            },
            {
                path: 'strava-webhook-list',
                component: StravaWebhookListComponent,
                canActivate: [AuthGuard, AdminGuard],
            },
            {
                path: 'coupon-edit/:partnerId/:id',
                component: CouponEditComponent,
                canActivate: [AuthGuard, AdminGuard],
            },
            {
                path: 'vestra-user-list',
                component: VestraUserListComponent,
                canActivate: [AuthGuard, AnyGuard],
            },
            {
                path: 'business-log-list',
                component: BusinessLogListComponent,
                canActivate: [AuthGuard, AdminGuard],
            },
            {
                path: 'version-info',
                component: VersionInfoComponent,
                canActivate: [AuthGuard, AnyGuard],
            },
            {
                path: 'notification',
                component: NotificationComponent,
                canActivate: [AuthGuard, AdminGuard],
            },
            {
                path: 'notification-create',
                component: NotificationCreateComponent,
                canActivate: [AuthGuard, AdminGuard],
            },
            {
                path: 'notification-create-email',
                component: NotificationCreateEmailComponent,
                canActivate: [AuthGuard, AdminGuard],
            },
            {
                path: 'notification-sent-message-list',
                component: NotificationSentMessageListComponent,
                canActivate: [AuthGuard, AdminGuard],
            },
            {
                path: 'user-coupon-report',
                component: UserCouponReportComponent,
                canActivate: [AuthGuard, AdminGuard],
            },
        ],
    },
    { path: '**', redirectTo: 'authentication/login' },
];

@NgModule({
    declarations: [AppComponent, MenuComponent, HomeComponent, LoginUserComponent, ForgotPasswordComponent, ResetPasswordComponent, ChangePasswordComponent, PartnerEditComponent, PartnerListComponent, StravaWebhookListComponent, StravaActivityViewComponent, CouponListComponent, CouponEditComponent, UserListComponent, UserEditComponent, CountryListComponent, SystemParameterListComponent, SystemParameterEditComponent, LanguageListComponent, LanguageEditComponent, ResourceListComponent, ResourceEditComponent, BusinessLogListComponent, VersionInfoComponent, VestraUserListComponent, VestraUserSearchComponent, CouponTabpageComponent, EmailEditComponent, EmailListComponent, FrontendDisplayHtmlEditComponent, FrontendDisplayHtmlListComponent, HtmlManagementEditComponent, AllCouponListComponent, PartnerTabpageComponent, PartnerListComponent, PartnerReportComponent, UserCouponReportComponent, VestraUserSelectComponent, NotificationComponent, NotificationCreateComponent, NotificationCreateEmailComponent,NotificationSentMessageListComponent, NotificationSentMessageEditComponent, NotificationResultComponent],
    imports: [
        CommonModule,
        BrowserModule,
        HttpClientModule,
        BrowserAnimationsModule,
        NgxJsonViewerModule,
        B2BWebShopSharedModule,
        ColorPickerModule.forRoot(),
        BsDatepickerModule.forRoot(),
        NgMultiSelectDropDownModule.forRoot(),
        TabsModule.forRoot(),
        TooltipModule.forRoot(),
        ModalModule.forRoot(),
        MarkdownModule.forRoot(),
        RouterModule.forRoot(routes),
        ToastrModule.forRoot({
            preventDuplicates: true,
            toastClass: 'custom-toastr',
        }),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: createTranslateLoader,
                deps: [HttpClient],
            },
            defaultLanguage: 'en',
        }),
        JwtModule.forRoot({
            config: {
                tokenGetter: tokenGetter,
                allowedDomains: [environment.allowedDomains],
                disallowedRoutes: [],
            },
        }),
        NgxMaskDirective,
        NgxMaskPipe,
        HammerModule,
        DataTablesModule,
    ],
    providers: [
        { provide: API_BASE_URL, useValue: environment.apiUrl },
        { provide: LIB_API_BASE_URL, useValue: environment.apiUrl },
        {
            provide: AuthenticationService_TOKEN,
            useClass: AuthenticationService,
        },
        { provide: VestraUserService_TOKEN, useClass: VestraUserService },
        { provide: CoreService_TOKEN, useClass: CoreService },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpErrorHandlerService,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: JsonCacheInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: SignalRHeaderInterceptor,
            multi: true,
        },
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler,
        },
        { provide: TooltipConfig, useFactory: getTooltipDefaultConfig },
        {
            provide: HAMMER_GESTURE_CONFIG,
            useClass: HammerConfig,
        },
        provideNgxMask(),
        DatePipe,
        CurrencyPipe,
        CookieService,
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
